<template>
  <ModuleWrapper :moduleId="moduleId" title="Enter Event Experience" @initializeData="initializeData">
    <template #main>
      <StudioToggleWithLabel
        id="show-onboarding-toggle"
        class="tw-mb-7"
        label="Mandatory Attendee Profiles"
        :subLabel="onboardingSubLabel"
        :checked="showOnboarding"
        :disabled="!showAttendeeProfilesEnabled"
        @update:checked="handleShowOnboardingToggle"
      />
      <StudioToggleWithLabel
        id="attendee-tech-check-toggle"
        label="Attendee Tech Check"
        subLabel="Require attendees to test their network strength and audio before entering the event."
        class="tw-mb-7"
        :checked="attendeeTechCheckEnabled"
        @update:checked="handleAttendeeTechCheckToggle"
      />
      <StudioToggleWithLabel
        id="enter-event-button-toggle"
        label="Allow Users to Enter Event Early"
        subLabel="This setting cannot be changed less than one hour before the start of the event. Turn this setting off to prevent users from entering your event space. 60 minutes before the event starts, users will all be able to enter the event space. "
        class="tw-mb-7"
        :checked="enterEventButtonEnabled"
        :title="
          isEnterEventButtonToggleDisabled
            ? 'This setting cannot be changed less than one hour before the start of the event'
            : ''
        "
        :disabled="isEnterEventButtonToggleDisabled"
        @update:checked="handleEnterEventButtonToggle"
      />
      <template v-if="!enterEventButtonEnabled">
        <StudioToggleWithLabel
          id="custom-cta-button-toggle"
          label="Custom Call to Action"
          subLabel="Include a button to link to the registration page or a custom URL."
          class="tw-mb-7"
          :checked="useCustomCtaButton"
          :title="
            isEnterEventButtonToggleDisabled
              ? 'This setting cannot be changed less than one hour before the start of the event'
              : ''
          "
          @update:checked="handleCustomCtaButtonToggle"
        />
        <StudioSelect
          id="cta-link-select"
          label="Link to"
          :options="[
            { value: 'registration', label: 'Registration Page' },
            { value: 'custom', label: 'Custom URL' }
          ]"
          size="small"
          orientation="horizontal"
          class="tw-mb-4"
          :disabled="!useCustomCtaButton"
          :value="selectedCustomCtaButton"
          @change="handleLinkToChange"
        />
        <div class="tw-grid tw-grid-cols-2 tw-gap-2">
          <StudioInput
            id="cta-button-text"
            label="Button Text"
            class="tw-mb-7"
            :disabled="!useCustomCtaButton"
            :maxlength="50"
            :modelValue="ctaButtonText"
            @update:modelValue="value => editCTAButtonText(value)"
          />
          <StudioInput
            v-if="selectedCustomCtaButton === 'custom'"
            id="cta-button-url"
            label="URL"
            class="tw-mb-7"
            :maxlength="50"
            :modelValue="ctaButtonUrl"
            placeholder="https://..."
            @update:modelValue="value => editCTAButtonUrl(value)"
          />
        </div>
      </template>
    </template>
  </ModuleWrapper>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { mapGetters, mapMutations } from 'vuex';

import ModuleWrapper from './ModuleWrapper.vue';

import { getRegistrationLink } from '@/studio/eventDashboard/NavigationUtils';
import { StudioToggleWithLabel, StudioInput } from '@/studioComponents';
import StudioSelect from '@/studioComponents/StudioSelect.vue';
import isEventAboutToStart from '@/utils/isEventAboutToStart';

export default defineComponent({
  name: 'SplashV2EnterEventExperience',
  components: {
    ModuleWrapper,
    StudioToggleWithLabel,
    StudioSelect,
    StudioInput
  },

  props: {
    moduleId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      enterEventButtonEnabled: false,
      attendeeTechCheckEnabled: true,
      showOnboarding: false,
      showAttendeeProfilesEnabled: false,
      useCustomCtaButton: false,
      ctaButtonText: '',
      ctaButtonUrl: '',
      selectedCustomCtaButton: ''
    };
  },
  mounted() {
    this.initializeData();
  },
  computed: {
    ...mapGetters('org', {
      organization: 'getCurrentOrg'
    }),
    ...mapGetters('studioEventList', {
      event: 'getCurrentEvent'
    }),
    ...mapGetters('branding', {
      getInitialModuleData: 'getInitialModuleData'
    }),
    ...mapGetters('studioEventList', {
      event: 'getCurrentEvent'
    }),
    isEnterEventButtonToggleDisabled(): boolean {
      if (!this.$route.params.eventId) {
        return false;
      }

      if (!this.event) {
        return true;
      }

      return isEventAboutToStart({
        startTime: this.event.start_time,
        timeInMinutes: 60
      });
    },
    registrationLink() {
      const eventId = this.$route.params.eventId as string;
      if (!eventId) {
        return '';
      }

      return getRegistrationLink(
        eventId,
        this.organization?.short_name,
        this.organization?.registration_app_vanity_domain_settings,
        this.event?.registration_app_domain,
        'events'
      );
    },
    isWebinar(): boolean {
      return this.event?.event_type === 'Webinar';
    },
    onboardingRestrictionReason(): string {
      return this.isWebinar
        ? 'This setting cannot be turned ON if Attendee Profiles is disabled in Edit Event > Settings.'
        : 'This setting cannot be turned ON if Attendee Profiles is disabled in the Stage > Stage Navigation.';
    },
    onboardingSubLabel(): string {
      return `Require attendees to create a profile when entering the event for the first time. ${this.onboardingRestrictionReason}`;
    }
  },
  methods: {
    ...mapMutations('branding', {
      setChangesMadeInEvent: 'setChangesMadeInEvent',
      setChangesMadeInThemeObj: 'setChangesMadeInThemeObj'
    }),
    initializeData() {
      const moduleKeys = [
        'disable_enter_event_button',
        'show_onboarding',
        'custom_cta_enabled',
        'custom_cta_link_to',
        'custom_cta_button_text',
        'custom_cta_button_url',
        'enable_tech_check',
        'show_attendee_profiles'
      ];

      const {
        disable_enter_event_button,
        show_onboarding,
        custom_cta_enabled,
        custom_cta_link_to,
        custom_cta_button_text,
        custom_cta_button_url,
        enable_tech_check,
        show_attendee_profiles
      } = this.getInitialModuleData(moduleKeys);

      this.enterEventButtonEnabled = !disable_enter_event_button;
      this.showAttendeeProfilesEnabled = !!show_attendee_profiles;
      this.showOnboarding = this.showAttendeeProfilesEnabled ? show_onboarding : false;
      this.useCustomCtaButton = custom_cta_enabled;
      this.selectedCustomCtaButton = custom_cta_link_to;
      this.ctaButtonText = custom_cta_button_text;
      this.ctaButtonUrl = custom_cta_button_url;
      this.attendeeTechCheckEnabled = enable_tech_check;
    },
    resetButton() {
      this.ctaButtonText = 'View Event Details';
      this.ctaButtonUrl = '';
      this.selectedCustomCtaButton = 'registration';
      this.ctaButtonUrl = this.registrationLink;
      this.setChangesMadeInThemeObj({
        custom_cta_button_text: 'View Event Details',
        custom_cta_link_to: 'registration',
        custom_cta_button_url: this.registrationLink
      });
    },
    handleEnterEventButtonToggle(e) {
      this.enterEventButtonEnabled = e.target.checked;
      this.setChangesMadeInEvent({
        disable_enter_event_button: !this.enterEventButtonEnabled
      });

      if (!this.enterEventButtonEnabled) {
        if (!this.selectedCustomCtaButton) {
          this.handleLinkToChange({ target: { value: 'registration' } });
          this.editCTAButtonUrl(this.registrationLink);
        }
        if (!this.ctaButtonText) {
          this.editCTAButtonText('View Event Details');
        }
      }
    },
    handleShowOnboardingToggle(e) {
      if (!this.showAttendeeProfilesEnabled) {
        this.showOnboarding = false;
        this.setChangesMadeInEvent({
          show_onboarding: false
        });
        return;
      }

      this.showOnboarding = e.target.checked;
      this.setChangesMadeInEvent({
        show_onboarding: this.showOnboarding
      });
    },
    handleCustomCtaButtonToggle(e) {
      this.useCustomCtaButton = e.target.checked;
      this.setChangesMadeInThemeObj({
        custom_cta_enabled: this.useCustomCtaButton
      });
      if (!this.useCustomCtaButton) {
        this.resetButton();
      }
    },
    handleLinkToChange(e) {
      this.selectedCustomCtaButton = e.target.value;
      this.setChangesMadeInThemeObj({
        custom_cta_link_to: this.selectedCustomCtaButton
      });
    },
    editCTAButtonText(value) {
      this.ctaButtonText = value;
      this.setChangesMadeInThemeObj({
        custom_cta_button_text: value
      });
    },
    editCTAButtonUrl(value) {
      this.ctaButtonUrl = value;
      this.setChangesMadeInThemeObj({
        custom_cta_button_url: value
      });
    },
    handleAttendeeTechCheckToggle(e) {
      this.attendeeTechCheckEnabled = e.target.checked;
      this.setChangesMadeInEvent({
        enable_tech_check: this.attendeeTechCheckEnabled
      });
    }
  }
});
</script>
